'use client';

import React from 'react';
import { usePathname, useParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import ServiceDetailHero from '@/components/sections/ServiceDetailHero';
import { getProductBySlug } from '@/lib/services';
import { baseUrl } from '@/lib/services';

export default function ProductClientLayout({
  children,
  params: initialParams,
}: {
  children: React.ReactNode;
  params: { slug?: string };
}) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  
  // Safely get the slug with fallback
  const slug = initialParams?.slug || params?.slug as string | undefined;
  
  // If no slug is provided, redirect to products page or show 404
  if (!slug) {
    router.push('/services');
    return null;
  }

  const product = getProductBySlug(slug);
  
  // Format the title from slug if product title is not available
  const formatTitleFromSlug = (slug: string) => {
    if (!slug) return 'Cloud Product';
    return slug
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  const title = product?.title || formatTitleFromSlug(slug);
  
  // If product is not found, you might want to handle this case
  if (!product) {
    // Option 1: Redirect to 404 or products page
    // router.push('/404');
    
    // Option 2: Show a not found message
    return (
      <div data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:50:6" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="50" data-component-end-line="61" data-component-file="ProductClientLayout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20flex%20items-center%20justify-center%20bg-gray-50%22%7D" className="min-h-screen flex items-center justify-center bg-gray-50">
        <div data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:51:8" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="51" data-component-end-line="60" data-component-file="ProductClientLayout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <h1 data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:52:10" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="52" data-component-end-line="52" data-component-file="ProductClientLayout.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-gray-800%20mb-2%22%2C%22textContent%22%3A%22Product%20Not%20Found%22%7D" className="text-2xl font-bold text-gray-800 mb-2">Product Not Found</h1>
          <p data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:53:10" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="53" data-component-end-line="53" data-component-file="ProductClientLayout.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-600%20mb-6%22%2C%22textContent%22%3A%22The%20product%20you're%20looking%20for%20doesn't%20exist%20or%20has%20been%20removed.%22%7D" className="text-gray-600 mb-6">The product you're looking for doesn't exist or has been removed.</p>
          <button data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:54:10" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="54" data-component-end-line="59" data-component-file="ProductClientLayout.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20bg-blue-600%20text-white%20rounded%20hover%3Abg-blue-700%20transition-colors%22%2C%22textContent%22%3A%22Back%20to%20Services%22%7D" 
            onClick={() => router.push('/services')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:66:4" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="66" data-component-end-line="99" data-component-file="ProductClientLayout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
      <ServiceDetailHero data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:67:6" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="67" data-component-end-line="71" data-component-file="ProductClientLayout.tsx" data-component-name="ServiceDetailHero" data-component-content="%7B%22elementName%22%3A%22ServiceDetailHero%22%7D"
        title={title}
        
        heroImage={product?.heroImage}
      />

      <main data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:73:6" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="73" data-component-end-line="75" data-component-file="ProductClientLayout.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22w-full%20bg-white%22%7D" className="w-full bg-white">
        {children}
      </main>
      
      {/* Schema.org JSON-LD for Product */}
      <script data-component-id="src/app/product/[slug]/ProductClientLayout.tsx:78:6" data-component-path="src/app/product/[slug]/ProductClientLayout.tsx" data-component-line="78" data-component-end-line="98" data-component-file="ProductClientLayout.tsx" data-component-name="script" data-component-content="%7B%22elementName%22%3A%22script%22%2C%22type%22%3A%22application%2Fld%2Bjson%22%7D"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": title,
            "description": product?.description || `Professional ${title} solutions`,
            "brand": {
              "@type": "Brand",
              "name": "Him Technology"
            },
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock",
              "priceCurrency": "INR",
              "url": `${baseUrl}${pathname}`
            }
          })
        }}
      />
    </div>
  );
}
